import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { updateProfile, changePassword } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Sprout, ArrowLeft, User, Lock, Save, Loader2, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [changingPw, setChangingPw] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), email: email.trim() });
      await checkAuth();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwSuccess(false);

    if (newPw !== confirmPw) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPw.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setChangingPw(true);
    try {
      await changePassword({ current_password: currentPw, new_password: newPw });
      setPwSuccess(true);
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  const backPath = user?.role === 'admin' ? '/admin' : '/dashboard';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5]">
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#A8E6CF]/30 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-[#5BA4A4] to-[#A8E6CF] rounded-xl flex items-center justify-center">
              <Sprout className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg font-bold text-[#2D4A4A]">My Profile</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(backPath)}
            className="text-[#5A7A7A]"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Details */}
        <Card className="border border-[#A8E6CF]/30 bg-white/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#2D4A4A] flex items-center gap-2">
              <User className="w-4 h-4 text-[#5BA4A4]" /> Personal Details
            </CardTitle>
            <CardDescription className="text-xs">Update your name or email address</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="profile-name" className="text-xs text-[#2D4A4A]">Full Name</Label>
                <Input
                  id="profile-name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] h-9 text-sm"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="profile-email" className="text-xs text-[#2D4A4A]">Email Address</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] h-9 text-sm"
                  data-testid="profile-email-input"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-[#5A7A7A]">
                <span>Role: <strong className="text-[#2D4A4A] capitalize">{user?.role}</strong></span>
              </div>
              <Button
                type="submit"
                size="sm"
                disabled={saving || (name === user?.name && email === user?.email)}
                className="bg-[#5BA4A4] hover:bg-[#4A9090] text-white"
                data-testid="save-profile-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save Changes
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border border-[#A8E6CF]/30 bg-white/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#2D4A4A] flex items-center gap-2">
              <Lock className="w-4 h-4 text-[#5BA4A4]" /> Change Password
            </CardTitle>
            <CardDescription className="text-xs">Enter your current password and choose a new one</CardDescription>
          </CardHeader>
          <CardContent>
            {pwSuccess && (
              <Alert className="mb-4 border-emerald-200 bg-emerald-50">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <AlertDescription className="text-emerald-700 text-sm">Password changed successfully.</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="current-pw" className="text-xs text-[#2D4A4A]">Current Password</Label>
                <Input
                  id="current-pw"
                  type="password"
                  value={currentPw}
                  onChange={e => setCurrentPw(e.target.value)}
                  required
                  className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] h-9 text-sm"
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-pw" className="text-xs text-[#2D4A4A]">New Password</Label>
                <Input
                  id="new-pw"
                  type="password"
                  value={newPw}
                  onChange={e => setNewPw(e.target.value)}
                  required
                  placeholder="At least 6 characters"
                  className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] h-9 text-sm"
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pw" className="text-xs text-[#2D4A4A]">Confirm New Password</Label>
                <Input
                  id="confirm-pw"
                  type="password"
                  value={confirmPw}
                  onChange={e => setConfirmPw(e.target.value)}
                  required
                  className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] h-9 text-sm"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                size="sm"
                disabled={changingPw || !currentPw || !newPw || !confirmPw}
                className="bg-[#5BA4A4] hover:bg-[#4A9090] text-white"
                data-testid="change-password-btn"
              >
                {changingPw ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Lock className="w-3.5 h-3.5 mr-1" />}
                Change Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProfilePage;
